type SortDirection = 'asc' | 'ascend' | 'desc' | 'descend';

/**
 * Sorts records numerically by a specified attribute
 *
 * @param a - First record to compare
 * @param b - Second record to compare
 * @param attr - Attribute name to sort by
 * @param dir - Sort direction ('asc', 'ascend', 'desc', 'descend')
 * @param forAntDTable - Whether sorting is for an AntD table that handles direction internally.
 *                       When true and direction is descending, returns ascending sort order
 *                       because AntD will invert the order again internally.
 * @param ignoreZero - Whether to ignore zero values when sorting
 * @returns Negative for a < b, positive for a > b, 0 for equal
 */
export const numericalSort = <T, K extends keyof T>(
  a: T | null | undefined,
  b: T | null | undefined,
  attr: K & string,
  dir: SortDirection = 'asc',
  forAntDTable = false,
  ignoreZero = false
): number => {
  // Define ascending direction constants
  const isAscending = ['asc', 'ascend'].includes(dir);

  // Adjust direction if using AntD table with internal sorting logic
  // For AntD tables: when using "desc" direction, we return ascending order
  // because AntD will invert it again internally
  const directionMultiplier = forAntDTable && !isAscending ? -1 : 1;

  // Helper function to determine sort order for a comparison result
  const getSortOrder = (compResult: number): number =>
    (isAscending ? compResult : -compResult) * directionMultiplier;

  // Handle null/undefined records
  if (!a || !b) {
    return getSortOrder(!a ? 1 : -1);
  }

  const aValue = a[attr];
  const bValue = b[attr];

  // Handle undefined/null attribute values
  if ((aValue === undefined || aValue === null) && aValue !== 0) {
    return getSortOrder(1);
  }
  if ((bValue === undefined || bValue === null) && bValue !== 0) {
    return getSortOrder(-1);
  }

  // Parse values to numbers
  const parsedA = parseFloat(String(aValue));
  const parsedB = parseFloat(String(bValue));

  // Handle NaN cases
  if (Number.isNaN(parsedA)) return directionMultiplier;
  if (Number.isNaN(parsedB)) return -1 * directionMultiplier;

  // Handle zero values if ignoreZero is true
  if (ignoreZero) {
    if (parsedA === 0) return directionMultiplier;
    if (parsedB === 0) return -1 * directionMultiplier;
  }

  // For equal values
  if (parsedA === parsedB) {
    return 0;
  }

  // Compare numeric values based on sort direction
  return getSortOrder(parsedA < parsedB ? -1 : 1);
};
